import { ParamMap } from '@angular/router';
import {
  CompanyPageSubMenu,
  CompanyPermission,
  VENDORS_MENU,
} from '../models/company-permission.model';
import { RoleConstant } from '../models/role-constant';
import { VendorListTabApi } from '../models/request/vendor-filters.model';
import {
  getCompanyPermissionForCompany,
  getPageUrlQueryParams,
  normalizeRoutePath,
  parsePageUrlParts,
} from './company-permission.util';

/** Query keys from BE pagePermissions (`vendors/list?{key}=`). */
export const VENDOR_LIST_TAB_QUERY_KEYS = ['myvendors', 'allvendors'] as const;

export type VendorListTabQueryKey = (typeof VENDOR_LIST_TAB_QUERY_KEYS)[number];

export const FIGMA_VENDOR_LIST_TAB_ORDER: readonly VendorListTabQueryKey[] = [
  'myvendors',
  'allvendors',
];

export const VENDOR_LIST_TAB_DISPLAY_LABELS: Record<
  VendorListTabQueryKey,
  string
> = {
  myvendors: 'My vendors',
  allvendors: 'All vendors',
};

export const VENDOR_TABS_HIDDEN_ROLE_IDS: readonly string[] = [
  RoleConstant.companyAdminId,
  RoleConstant.accountsPayableManager2,
  RoleConstant.accountsPayableCreator,
  RoleConstant.RMmanager,
];

export interface VendorListTabViewModel {
  page: string;
  pageUrl: string;
  queryParams: Record<string, string>;
  queryKey: VendorListTabQueryKey | null;
  apiTab: VendorListTabApi;
}

export const VENDOR_LIST_DEFAULT_PAGE_SIZE = 10;

const LEGACY_VENDOR_LIST_TABS: CompanyPageSubMenu[] = [
  { page: 'My Vendors', pageUrl: 'vendors/list?myvendors=' },
  { page: 'All Vendors', pageUrl: 'vendors/list?allvendors=' },
];

export function isVendorListTabPageUrl(pageUrl: string): boolean {
  const { path, queryParams } = parsePageUrlParts(pageUrl);
  if (path.toLowerCase() !== 'vendors/list' || !queryParams) {
    return false;
  }
  return VENDOR_LIST_TAB_QUERY_KEYS.some((key) =>
    Object.prototype.hasOwnProperty.call(queryParams, key)
  );
}

export function getVendorListTabSubMenus(
  companyPermission: CompanyPermission | null | undefined
): CompanyPageSubMenu[] {
  const pagePermission = companyPermission?.pagePermissions?.find(
    (entry) => entry.menu === VENDORS_MENU
  );

  const fromApi = (pagePermission?.subMenus ?? []).filter((subMenu) =>
    isVendorListTabPageUrl(subMenu.pageUrl)
  );

  return fromApi.length ? fromApi : LEGACY_VENDOR_LIST_TABS;
}

export function resolveVendorListTabQueryKey(
  query: ParamMap | URLSearchParams | null | undefined
): VendorListTabQueryKey | null {
  if (!query) {
    return null;
  }

  const hasKey = (key: string) =>
    query instanceof URLSearchParams ? query.has(key) : query.has(key);

  return VENDOR_LIST_TAB_QUERY_KEYS.find((key) => hasKey(key)) ?? null;
}

export function resolveVendorApiTabFromQueryKey(
  queryKey: VendorListTabQueryKey | null
): VendorListTabApi {
  return queryKey === 'allvendors' ? 'AllVendor' : 'MyVendor';
}

export function resolveVendorSavedFilterTabKey(
  queryKey: VendorListTabQueryKey | null
): VendorListTabApi {
  return resolveVendorApiTabFromQueryKey(queryKey);
}

export function getVendorListTabs(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined,
  selectedRoleId?: string | null
): VendorListTabViewModel[] {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );

  let subMenus = getVendorListTabSubMenus(companyPermission);

  if (
    selectedRoleId &&
    VENDOR_TABS_HIDDEN_ROLE_IDS.includes(selectedRoleId)
  ) {
    subMenus = subMenus.filter((subMenu) => {
      const queryParams = getPageUrlQueryParams(subMenu.pageUrl) ?? {};
      return Object.prototype.hasOwnProperty.call(queryParams, 'myvendors');
    });
  }

  return subMenus.map((subMenu) => toVendorListTabViewModel(subMenu));
}

function toVendorListTabViewModel(
  subMenu: CompanyPageSubMenu
): VendorListTabViewModel {
  const queryParams = getPageUrlQueryParams(subMenu.pageUrl) ?? {};
  const queryKey =
    VENDOR_LIST_TAB_QUERY_KEYS.find((key) =>
      Object.prototype.hasOwnProperty.call(queryParams, key)
    ) ?? null;

  return {
    page: subMenu.page,
    pageUrl: subMenu.pageUrl,
    queryParams,
    queryKey,
    apiTab: resolveVendorApiTabFromQueryKey(queryKey),
  };
}

export function sortVendorListTabsForDesign(
  tabs: VendorListTabViewModel[]
): VendorListTabViewModel[] {
  return [...tabs]
    .sort((a, b) => {
      const ai = a.queryKey
        ? FIGMA_VENDOR_LIST_TAB_ORDER.indexOf(a.queryKey)
        : -1;
      const bi = b.queryKey
        ? FIGMA_VENDOR_LIST_TAB_ORDER.indexOf(b.queryKey)
        : -1;
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    })
    .map((tab) => {
      const label =
        tab.queryKey && VENDOR_LIST_TAB_DISPLAY_LABELS[tab.queryKey]
          ? VENDOR_LIST_TAB_DISPLAY_LABELS[tab.queryKey]
          : tab.page;
      return { ...tab, page: label };
    });
}

export function sortVendorNavSubMenusForDesign(
  subMenus: CompanyPageSubMenu[]
): CompanyPageSubMenu[] {
  const decorated = (subMenus ?? []).map((subMenu, index) => {
    const queryParams = getPageUrlQueryParams(subMenu.pageUrl) ?? {};
    const queryKey =
      VENDOR_LIST_TAB_QUERY_KEYS.find((key) =>
        Object.prototype.hasOwnProperty.call(queryParams, key)
      ) ?? null;
    const order = queryKey
      ? FIGMA_VENDOR_LIST_TAB_ORDER.indexOf(queryKey)
      : -1;
    const page =
      queryKey && VENDOR_LIST_TAB_DISPLAY_LABELS[queryKey]
        ? VENDOR_LIST_TAB_DISPLAY_LABELS[queryKey]
        : subMenu.page;
    return { subMenu: { ...subMenu, page }, order, index };
  });

  return decorated
    .sort((a, b) => {
      const ai = a.order === -1 ? 1000 + a.index : a.order;
      const bi = b.order === -1 ? 1000 + b.index : b.order;
      return ai - bi;
    })
    .map((entry) => entry.subMenu);
}

export function shouldShowVendorListTabs(
  tabs: VendorListTabViewModel[],
  selectedRoleId: string | null | undefined
): boolean {
  if (tabs.length <= 1) {
    return false;
  }
  if (selectedRoleId && VENDOR_TABS_HIDDEN_ROLE_IDS.includes(selectedRoleId)) {
    return false;
  }
  return true;
}

export function resolveForcedVendorApiTab(
  selectedRoleId: string | null | undefined
): VendorListTabApi | null {
  if (selectedRoleId && VENDOR_TABS_HIDDEN_ROLE_IDS.includes(selectedRoleId)) {
    return 'MyVendor';
  }
  return null;
}

export function isVendorListTabActive(
  tab: VendorListTabViewModel,
  pathname: string,
  query: URLSearchParams | ParamMap | null | undefined
): boolean {
  const normalizedPath = normalizeRoutePath(pathname);
  if (
    normalizedPath !== 'vendors/list' &&
    normalizedPath !== 'vendor/list' &&
    !normalizedPath.startsWith('vendors/list') &&
    !normalizedPath.startsWith('vendor/list')
  ) {
    return false;
  }

  const activeKey = resolveVendorListTabQueryKey(query);
  if (tab.queryKey && activeKey) {
    return tab.queryKey === activeKey;
  }

  if (!tab.queryKey && !activeKey) {
    return true;
  }

  return false;
}

export function isVendorSubMenuRoute(pageUrl: string): boolean {
  const normalized = parsePageUrlParts(pageUrl).path.toLowerCase();
  return normalized === 'vendors/list' || normalized.startsWith('vendors/');
}

export function isVendorSubMenuRouteActive(
  pageUrl: string,
  currentPathname: string,
  currentQuery?: URLSearchParams | null
): boolean {
  const { path: pagePath, queryParams } = parsePageUrlParts(pageUrl);
  const normalizedPageUrl = pagePath.toLowerCase();
  const normalizedCurrentPath = normalizeRoutePath(currentPathname);

  const pathMatches =
    normalizedPageUrl === 'vendors/list'
      ? normalizedCurrentPath.startsWith('vendors') ||
        normalizedCurrentPath.startsWith('vendor')
      : normalizedCurrentPath === normalizedPageUrl ||
        normalizedCurrentPath.startsWith(`${normalizedPageUrl}/`);

  if (!pathMatches) {
    return false;
  }

  if (!queryParams) {
    return true;
  }

  if (
    normalizedPageUrl === 'vendors/list' &&
    normalizedCurrentPath !== 'vendors/list' &&
    normalizedCurrentPath !== 'vendor/list'
  ) {
    return false;
  }

  if (!currentQuery) {
    return false;
  }

  return Object.keys(queryParams).every((key) => currentQuery.has(key));
}
